import { ReactNode, createContext, useEffect, useState } from 'react';
import { THEMES } from 'greener-ui-library';

export type Settings = {
  direction: string;
  responsiveFontSizes: boolean;
  theme: string;
};

type SettingsContextValue = {
  settings: Settings;
  saveSettings: (update?: Partial<Settings>) => void;
};

const defaultSettings: Settings = {
  direction: 'ltr',
  responsiveFontSizes: true,
  theme: THEMES.LIGHT,
};

export const restoreSettings = (): Settings | null => {
  let settings: Settings | null = null;

  try {
    const storedData = window.localStorage.getItem('settings');

    if (storedData) {
      settings = JSON.parse(storedData);
    }
  } catch (err) {
    console.error(err);
    // If stored data is not a strigified JSON this will fail,
    // that's why we catch the error
  }

  return settings;
};

export const storeSettings = (settings: Settings) => {
  window.localStorage.setItem('settings', JSON.stringify(settings));
};

const SettingsContext = createContext<SettingsContextValue>({
  settings: defaultSettings,
  saveSettings: () => {},
});

type SettingsProviderProps = {
  settings?: Settings | null;
  children: ReactNode;
};

export const SettingsProvider = ({ settings, children }: SettingsProviderProps) => {
  const [currentSettings, setCurrentSettings] = useState<Settings>(() => ({ ...defaultSettings, ...settings }));

  const handleSaveSettings = (update: Partial<Settings> = {}) => {
    const mergedSettings = { ...currentSettings, ...update };

    setCurrentSettings(mergedSettings);
    storeSettings(mergedSettings);
  };

  useEffect(() => {
    document.dir = currentSettings.direction;
  }, [currentSettings]);

  return (
    <SettingsContext.Provider
      value={{
        settings: currentSettings,
        saveSettings: handleSaveSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const SettingsConsumer = SettingsContext.Consumer;

export default SettingsContext;
